<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import ReviewSummary from '../components/ReviewSummary.vue';
import { useFeedback } from '../composables/useFeedback';
import { useMarkReviewed, useReview } from '../composables/useReviews';
import { errorDetail } from '../utils/error';
import type { ReviewType } from '../api';

const props = defineProps<{ reviewType: ReviewType; id: string }>();

const router = useRouter();
const reviewType = computed(() => props.reviewType);
const id = computed(() => props.id);
const { data: review, isPending, error } = useReview(reviewType, id);

const { notify } = useFeedback();
const markReviewed = useMarkReviewed(reviewType, id);
function approve() {
  if (!review.value || markReviewed.isPending.value) return;
  markReviewed.mutate(undefined, {
    onSuccess: () => {
      notify({ tone: 'success', summary: 'Marked as reviewed' });
      void router.push({ name: 'reviews', params: { reviewType: reviewType.value } });
    },
    onError: (err) => notify({ tone: 'error', summary: 'Failed to mark as reviewed', detail: errorDetail(err) }),
  });
}
</script>

<template>
  <div>
    <RouterLink :to="{ name: 'reviews', params: { reviewType } }">&laquo; Back</RouterLink>

    <v-alert v-if="error" type="error" :text="errorDetail(error)" class="mt-4" />
    <v-progress-linear v-else-if="isPending" indeterminate class="mt-4" />
    <ReviewSummary
      v-else-if="review"
      class="mt-4"
      :review="review"
      :approving="markReviewed.isPending.value"
      @approve="approve"
    />
  </div>
</template>
